import { useMutation } from '@tanstack/react-query'
import { refreshToken } from 'api/auth/auth.api'
import { AuthResult } from 'api/auth/auth.codecs'
import { env } from 'app/env'
import constate from 'constate'
import { addMinutes, differenceInSeconds } from 'date-fns'
import { useEffectOnce } from 'hooks/use-effect-once'
import { useCallback, useEffect, useRef, useState } from 'react'

// eslint-disable-next-line fp/no-let
export let globalAccessToken: string | null = null

export const useLogoutAcrossTabs = (callback: () => void) => {
  useEffect(() => {
    const syncLogout = (event: StorageEvent) => {
      if (event.key === 'logout') {
        callback()
      }
    }

    window.addEventListener('storage', syncLogout)

    return () => {
      window.removeEventListener('storage', syncLogout)
    }
  }, [callback])

  return useCallback(() => {
    callback()
    localStorage.setItem('logout', Date.now().toString())
  }, [callback])
}

type AuthState =
  | { type: 'initial'; expiresInSeconds?: undefined }
  | {
      type: 'authenticated'
      accessToken: string
      expiresInSeconds: number
    }
  | { type: 'unauthenticated'; expiresInSeconds?: undefined }

const useAuthHook = () => {
  const [auth, setAuth] = useState<AuthState>({ type: 'initial' })

  const isAuthenticated = auth.type === 'authenticated'
  const isInitial = auth.type === 'initial'
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const removeToken = useLogoutAcrossTabs(
    useCallback(() => {
      setAuth({ type: 'unauthenticated' })
      globalAccessToken = null

      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
      }

      localStorage.removeItem('USER_ACCEPTED_USAGE_TERMS')
      localStorage.removeItem('USER_ACCEPTED_BOOKING_TERMS')
    }, []),
  )

  const setAuthData = ({ accessToken, accessTokenExpiredAt }: AuthResult) => {
    globalAccessToken = accessToken

    const expiresInSeconds = differenceInSeconds(
      accessTokenExpiredAt,
      addMinutes(new Date(), new Date().getTimezoneOffset()),
    )

    setAuth({
      accessToken,
      expiresInSeconds,
      type: 'authenticated',
    })
  }

  const { mutate: mutateRefresh } = useMutation(refreshToken, {
    onError: removeToken,
    onSuccess: setAuthData,
  })

  const refresh = useCallback(
    () =>
      mutateRefresh({
        clientSecret: env.REACT_APP_APPLICATION_ID,
      }),
    [mutateRefresh],
  )

  useEffectOnce(refresh)

  useEffect(() => {
    if (isAuthenticated && auth.expiresInSeconds)
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null
        refresh()
      }, (auth.expiresInSeconds - 60) * 1000)
  }, [auth.expiresInSeconds, isAuthenticated, refresh])

  return {
    isAuthenticated,
    isInitial,
    ...(isAuthenticated
      ? {
          accessToken: auth.accessToken,
        }
      : undefined),
    removeToken,
    setAuthData,
  } as const
}

export const [AuthProvider, useAuth] = constate(useAuthHook)
